// src/components/ResidentialDropdowns.js

import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  FormHelperText,
  OutlinedInput,
} from "@mui/material";
import { Controller, useForm, useFormContext } from "react-hook-form";
import {
  getCounties,
  getSubCounties,
  getConstituencies,
  getWards,
  getSubCountiesInCounty,
  getLocalitiesInCounty,
  getAreasInLocality,
} from "kenya-locations";

const ResidentialDropdowns = () => {
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();

  const [counties, setCounties] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [wards, setWards] = useState([]);

  const selectedCounty = watch("county");
  const selectedConstituency = watch("constituency");
  const selectedSubCounty = watch("subcounty");

  useEffect(() => {
    setCounties(getCounties());
  }, []);

  // Effect to update Sub-County and Constituency lists when County changes
  useEffect(() => {
    if (selectedCounty) {
      setSubCounties(getSubCountiesInCounty(selectedCounty));
      setConstituencies(getLocalitiesInCounty(selectedCounty));
      // Reset all dependent fields
      setValue("subcounty", "");
      setValue("constituency", "");
      setValue("ward", "");
    } else {
      setSubCounties([]);
      setConstituencies([]);
    }
  }, [selectedCounty, setValue]);

  // Main logic: When Constituency is selected, try to auto-select the Sub-County
  useEffect(() => {
    if (selectedConstituency && subCounties.length > 0) {
      // Check if a sub-county with the exact same name exists
      const matchingSubCounty = subCounties.find(
        (sc) => sc === selectedConstituency
      );
      if (matchingSubCounty) {
        setValue("subcounty", matchingSubCounty, { shouldValidate: true });
      }

      // Populate wards based on the selected constituency
      setWards(getAreasInLocality(selectedConstituency));
      setValue("ward", "");
    } else {
      setWards([]);
    }
  }, [selectedCounty, selectedConstituency, subCounties, setValue]);

  return (
    <>
      {/* County Dropdown */}
      <Grid item xs={12} sx={{ paddingTop: "20px" }}>
        <FormControl fullWidth error={!!errors.county}>
          <InputLabel>County</InputLabel>
          <Select {...register("county")} label="County">
            {counties.map((county) => (
              <MenuItem key={county.code} value={county.name}>
                {county.name}
              </MenuItem>
            ))}
          </Select>
          {errors.county && (
            <FormHelperText>{errors.county.message}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Constituency Dropdown (Primary user choice after county) */}
      <Grid item xs={6} sx={{ paddingTop: "20px" }}>
        <FormControl fullWidth error={!!errors.constituency}>
          <InputLabel>Constituency</InputLabel>
          <Select
            {...register("constituency")}
            label="Constituency"
            disabled={!selectedCounty}
          >
            {constituencies.map((constituency) => (
              <MenuItem key={constituency.code} value={constituency.name}>
                {constituency.name}
              </MenuItem>
            ))}
          </Select>
          {errors.constituency && (
            <FormHelperText>{errors.constituency.message}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Sub-County Dropdown (Often auto-populated) */}
      <Grid item xs={6} sx={{ paddingTop: "20px" }}>
        <FormControl fullWidth error={!!errors.subcounty}>
          <InputLabel>Sub-County</InputLabel>
          <Select
            {...register("subcounty")}
            label="Sub-County"
            disabled={!selectedCounty}
          >
            {subCounties.map((subcounty) => (
              <MenuItem key={subcounty.code} value={subcounty.name}>
                {subcounty.name}
              </MenuItem>
            ))}
          </Select>
          {errors.subcounty && (
            <FormHelperText>{errors.subcounty.message}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Ward Dropdown */}
      <Grid item xs={6} sx={{ paddingTop: "20px" }}>
        <FormControl fullWidth error={!!errors.ward}>
          <InputLabel>Ward</InputLabel>
          <Select
            {...register("ward")}
            label="Ward"
            disabled={!selectedConstituency}
          >
            {wards.map((ward) => (
              <MenuItem key={ward.code} value={ward.name}>
                {ward.name}
              </MenuItem>
            ))}
          </Select>
          {errors.ward && (
            <FormHelperText>{errors.ward.message}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Village / Estate Field */}
      <Grid item xs={6} sx={{ paddingTop: "20px" }}>
        <TextField
          {...register("area")}
          fullWidth
          label="Town / Village / Estate"
          variant="outlined"
          error={!!errors.village}
          helperText={errors.village?.message}
        />
      </Grid>
    </>
  );
};

export default ResidentialDropdowns;
